'use client';
import { Button, Checkbox, Modal } from '@ethsign/ui';
import { ethPrivateKeyDecrypt, ethPublicKeyEncrypt, ethSignAesDecrypt } from '@ethsign/utils-web';
import classNames from 'classnames';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { ColorAlertIcon, ColorShieldIcon, PasswordInput } from '@/components';
import { updateEncryptedContractPassword } from '@/services';
import { ICurrentUser } from '@/types';
import { WalletType } from '@/utils';
import { getUserWalletType } from '@/utils/auth';
import { WalletFactory } from '@/utils/wallet/WalletFactory';
type Props = {
  onOpenChange: (open: boolean) => void;
  onConfirm: (value: string) => Promise<boolean>;
  open: boolean;
  hiddenCloseIcon?: boolean;
  footer?: React.ReactNode;
  user?: ICurrentUser;
  encryptedContractPassword?: string;
  hidePasswordInput?: boolean;
  id?: string;
  onClose?: () => void;
  isShowAutoOpenPasswordManager?: boolean;
};
export const DecryptContractModal = (props: Props) => {
  const {
    onConfirm,
    open,
    onOpenChange,
    hiddenCloseIcon,
    footer,
    user,
    encryptedContractPassword,
    hidePasswordInput,
    id,
    onClose,
    isShowAutoOpenPasswordManager = true
  } = props;

  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shownPasswordManager, setShownPasswordManager] = useState(false);
  const [shownMasterPasswordManager, setShownMasterPasswordManager] = useState(false);
  const [openContractPasswordManager, setAutoOpenContractPasswordManager] = useState(true);
  const [isInited, setIsInited] = useState(false);
  const getPasswordByWallet = async () => {
    setLoading(true);
    try {
      const walletType = getUserWalletType(user!.chainType as WalletType, user?.authType);
      const wallet = await WalletFactory.getWallet(walletType, user!.address);
      const isConnected = await wallet.connect(user?.authType);
      if (!isConnected) return false;
      const signData = await wallet.safeSign(user?.identitySeed || '');
      const privateKey = await ethSignAesDecrypt(user?.encryptedPKByWallet || '', signData.signature.slice(0, 32));
      const password = await ethPrivateKeyDecrypt(privateKey, encryptedContractPassword || '');
      await onConfirm(password);
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };
  const getPasswordByMasterPassword = async () => {
    try {
      const privateKey = await ethSignAesDecrypt(user?.encryptedPKByPassword || '', value);
      const password = await ethPrivateKeyDecrypt(privateKey, encryptedContractPassword || '');
      await onConfirm(password);
      setShownMasterPasswordManager(false);
      setError(false);
    } catch (error) {
      setError(true);
    }
    setValue('');
  };
  const enabledPasswordManager = user?.enableIdentity && !!user.encryptedPKByWallet && !!encryptedContractPassword;
  const enabledMasterPasswordManager =
    user?.enableIdentity && !!user.encryptedPKByPassword && !!encryptedContractPassword;
  useEffect(() => {
    if (!open) return;
    if (enabledPasswordManager) {
      setShownPasswordManager(true);
      getPasswordByWallet();
    }
    if (enabledMasterPasswordManager) {
      setShownMasterPasswordManager(true);
    }
    setIsInited(true);
  }, [enabledPasswordManager, enabledMasterPasswordManager, open]);
  const onDecrypt = async (value: string) => {
    const result = await onConfirm(value);
    if (
      result &&
      !encryptedContractPassword &&
      openContractPasswordManager &&
      user?.enableIdentity &&
      user.identityPublicKey &&
      isShowAutoOpenPasswordManager
    ) {
      const encryptedContractPassword = await ethPublicKeyEncrypt(user.identityPublicKey, value);
      await updateEncryptedContractPassword({ encryptedContractPassword, id: id as string });
    }
    setError(!result);
    setValue('');
  };
  return (
    <>
      <Modal
        onClose={onClose}
        hiddenCloseIcon={hiddenCloseIcon}
        open={open && !shownPasswordManager && !shownMasterPasswordManager && isInited}
        onOpenChange={(open: boolean) => {
          onOpenChange(open);
          if (!open) {
            setValue('');
            setError(false);
          }
        }}
        maskClosable={false}
        footer={
          <>
            <Button
              className={'w-full'}
              onClick={async () => {
                onDecrypt(value);
              }}
            >
              Decrypt
            </Button>
            {enabledPasswordManager && (
              <Button
                className="flex-1"
                onClick={async () => {
                  setShownPasswordManager(true);
                  setError(false);
                  setValue('');
                }}
                variant="outline"
              >
                Decrypt with Password Manager
              </Button>
            )}
            {enabledMasterPasswordManager && (
              <Button
                className="flex-1"
                onClick={async () => {
                  setShownMasterPasswordManager(true);
                  setError(false);
                  setValue('');
                }}
                variant="outline"
              >
                Input Master Password
              </Button>
            )}
            {footer}
            {!enabledPasswordManager &&
              !enabledMasterPasswordManager &&
              user?.enableIdentity &&
              isShowAutoOpenPasswordManager && (
                <div className="flex flex-row items-center">
                  <Checkbox
                    checked={openContractPasswordManager}
                    onCheckedChange={(value: boolean) => {
                      setAutoOpenContractPasswordManager(value);
                    }}
                  ></Checkbox>
                  <span className="ml-2 text-md font-normal text-gray-700">
                    Enable password manager for this contract
                  </span>
                </div>
              )}
          </>
        }
        className={'md:w-[410px]'}
      >
        <div className="flex items-center flex-col justify-center">
          <ColorShieldIcon />
          <div className="text-lg font-medium text-gray-900 mt-[20px]">🔐 Password-protected Contents</div>
          <div className="text-sm text-gray-500 mt-[8px] mb-[20px]  text-center">
            Enter contract password to decrypt the contract. Contract passwords are set by sender.
          </div>
          <PasswordInput
            className={classNames({
              'border-red-500': error,
              'focus:border-red-500': error
            })}
            value={value}
            onChange={(e: any) => setValue(e.target.value)}
            onKeyDown={async (e: any) => {
              if (e.key === 'Enter') {
                onDecrypt(e.target.value);
              }
            }}
          />
          {error && (
            <div className="text-sm font-normal text-red-500 mt-[6px]">
              Incorrect password. Please contact the contract sender and retry.
            </div>
          )}
        </div>
      </Modal>
      <Modal
        onClose={onClose}
        open={open && shownPasswordManager}
        onOpenChange={(open: boolean) => {
          setShownPasswordManager(open);
          hidePasswordInput && onOpenChange(false);
        }}
        hiddenCloseIcon={hiddenCloseIcon}
        maskClosable={false}
        footer={
          loading ? (
            <span></span>
          ) : (
            <div className="w-full flex flex-row justify-between">
              {!hidePasswordInput && (
                <Button
                  className="flex-1"
                  onClick={async () => {
                    setShownPasswordManager(false);
                  }}
                  variant="outline"
                >
                  Input Password
                </Button>
              )}
              <Button className="flex-1 ml-3" onClick={() => getPasswordByWallet()}>
                Retry
              </Button>
            </div>
          )
        }
        className={'md:w-[400px]'}
      >
        <div className="flex flex-col items-center justify-center">
          {loading ? <Loader2 className="animate-spin" color="#CF5C10" size={40} /> : <ColorAlertIcon />}
          <div className="text-lg font-medium text-gray-900 my-2 text-center">
            Decrypting with <br /> EthSign Password Manager
          </div>
          <div className="text-sm font-normal text-gray-500 text-center">
            Please sign the message with your wallet to decrypt the contract with EthSign Password Manager.
          </div>
        </div>
      </Modal>
      <Modal
        onClose={onClose}
        open={open && shownMasterPasswordManager}
        onOpenChange={(open: boolean) => {
          setShownMasterPasswordManager(open);
          if (!open) {
            setValue('');
            setError(false);
            hidePasswordInput && onOpenChange(false);
          }
        }}
        hiddenCloseIcon={hiddenCloseIcon}
        maskClosable={false}
        footer={
          <>
            <Button
              className={'w-full'}
              onClick={async () => {
                await getPasswordByMasterPassword();
              }}
            >
              Decrypt Contract
            </Button>
            {!hidePasswordInput && (
              <Button
                className={'w-full'}
                variant="outline"
                onClick={async () => {
                  setShownMasterPasswordManager(false);
                  setError(false);
                  setValue('');
                }}
              >
                Input Contract Password
              </Button>
            )}
          </>
        }
        className={'md:w-[400px]'}
      >
        <div className="flex items-center flex-col justify-center">
          <ColorShieldIcon />
          <div className="text-lg font-medium text-gray-900 mt-[20px]">Please enter your master password</div>
          <div className="text-sm text-gray-500 mt-[8px] mb-[20px]  text-center">
            Input the master password you set for this account to decrypt the contract.
          </div>
          <PasswordInput
            className={classNames({
              'border-red-500': error,
              'focus:border-red-500': error
            })}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={async (e: any) => {
              if (e.key === 'Enter') {
                await getPasswordByMasterPassword();
              }
            }}
          />
          {error && (
            <div className="text-sm font-normal text-red-500 mt-[6px]">
              Incorrect password. Please try again or input contract password instead.
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
